import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { UserCheck, MapPin, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LiveNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [isVisible, setIsVisible] = useState(true);

  // Sample notification data for demo purposes when no real data
  const sampleNotifications = [
    // UK Locations (original)
    { name: "Sarah M.", location: "London, UK", course: "Pro", time: "2 minutes ago" },
    { name: "James R.", location: "Manchester, UK", course: "Foundation", time: "5 minutes ago" },
    { name: "Emma L.", location: "Edinburgh, Scotland", course: "Pro", time: "8 minutes ago" },
    { name: "Michael K.", location: "Birmingham, UK", course: "Foundation", time: "12 minutes ago" },
    { name: "Lucy P.", location: "Bristol, UK", course: "Pro", time: "15 minutes ago" },
    
    // Global Locations
    { name: "Amanda S.", location: "New York, USA", course: "Pro", time: "3 minutes ago" },
    { name: "Carlos M.", location: "Toronto, Canada", course: "Foundation", time: "7 minutes ago" },
    { name: "Sophie D.", location: "Paris, France", course: "Pro", time: "11 minutes ago" },
    { name: "Hans W.", location: "Berlin, Germany", course: "Foundation", time: "14 minutes ago" },
    { name: "Maria G.", location: "Madrid, Spain", course: "Pro", time: "18 minutes ago" },
    { name: "Luca R.", location: "Rome, Italy", course: "Foundation", time: "21 minutes ago" },
    { name: "Yuki T.", location: "Tokyo, Japan", course: "Pro", time: "24 minutes ago" },
    { name: "Priya S.", location: "Mumbai, India", course: "Foundation", time: "27 minutes ago" },
    { name: "Chen W.", location: "Singapore", course: "Pro", time: "30 minutes ago" },
    { name: "Olivia R.", location: "Sydney, Australia", course: "Foundation", time: "33 minutes ago" },
    { name: "Ahmed H.", location: "Dubai, UAE", course: "Pro", time: "36 minutes ago" },
    { name: "Isabella C.", location: "São Paulo, Brazil", course: "Foundation", time: "39 minutes ago" },
    { name: "Erik N.", location: "Stockholm, Sweden", course: "Pro", time: "42 minutes ago" },
    { name: "Anna K.", location: "Amsterdam, Netherlands", course: "Foundation", time: "45 minutes ago" },
    { name: "Ryan O.", location: "Dublin, Ireland", course: "Pro", time: "48 minutes ago" },
    { name: "Fatima A.", location: "Casablanca, Morocco", course: "Foundation", time: "51 minutes ago" },
    { name: "David L.", location: "Cape Town, South Africa", course: "Pro", time: "54 minutes ago" },
  ];

  // Fetch real signup notifications from backend
  const fetchRecentSignups = async () => {
    try {
      const response = await axios.get(`${API}/recent-signups`);
      const realNotifications = response.data.notifications || [];
      
      if (realNotifications.length > 0) {
        setNotifications(realNotifications.slice(0, 3));
      } else {
        // Use sample data if no real signups
        setNotifications(sampleNotifications.slice(0, 3));
      }
    } catch (error) {
      console.error('Error fetching recent signups:', error);
      // Fallback to sample data
      setNotifications(sampleNotifications.slice(0, 3));
    }
  };

  useEffect(() => {
    // Initial fetch
    fetchRecentSignups();

    // Fetch real data every 30 seconds
    const realDataInterval = setInterval(fetchRecentSignups, 30000);

    // Simulate additional notifications for demo purposes
    const demoInterval = setInterval(() => {
      const randomNotification = sampleNotifications[Math.floor(Math.random() * sampleNotifications.length)];
      const newNotification = {
        ...randomNotification,
        id: Date.now(),
        time: "Just now"
      };

      setNotifications(prev => {
        const updated = [newNotification, ...prev.slice(0, 4)]; // Keep only 5 notifications
        return updated;
      });

      // Auto-hide after 8 seconds
      setTimeout(() => {
        setNotifications(prev => prev.filter(n => n.id !== newNotification.id));
      }, 8000);
    }, Math.random() * 30000 + 20000); // Random interval between 20-50 seconds

    return () => {
      clearInterval(realDataInterval);
      clearInterval(demoInterval);
    };
  }, []);

  // Function to add real signup notification (called from enrollment flow)
  const addRealSignupNotification = async (studentData) => {
    try {
      // The backend now handles location detection
      const realNotification = {
        id: Date.now(),
        name: `${studentData.first_name} ${studentData.last_name.charAt(0)}.`,
        location: studentData.signup_location || "UK",
        course: studentData.course_type || "Enrollment",
        time: "Just now"
      };

      setNotifications(prev => [realNotification, ...prev.slice(0, 4)]);

      // Auto-hide after 10 seconds
      setTimeout(() => {
        setNotifications(prev => prev.filter(n => n.id !== realNotification.id));
      }, 10000);
    } catch (error) {
      console.error('Error adding signup notification:', error);
    }
  };

  // Expose function globally so it can be called from enrollment components
  useEffect(() => {
    window.addSignupNotification = addRealSignupNotification;
    return () => {
      delete window.addSignupNotification;
    };
  }, []);

  if (!isVisible || notifications.length === 0) {
    return null;
  }

  return (
    <div className="fixed bottom-6 left-6 z-50 space-y-3 max-w-sm">
      {notifications.map((notification, index) => (
        <Card 
          key={notification.id || index}
          className="bg-white/95 backdrop-blur-sm border-l-4 border-l-green-500 shadow-lg p-4 transform transition-all duration-500 animate-slide-in-left hover:shadow-xl"
          style={{
            animation: `slideInLeft 0.5s ease-out ${index * 0.1}s both`
          }}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <UserCheck className="w-5 h-5 text-green-600" />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-1">
                  <p className="font-semibold text-slate-800 text-sm">
                    {notification.name}
                  </p>
                  <Badge 
                    className={`text-xs ${
                      notification.course === 'Pro' 
                        ? 'bg-purple-100 text-purple-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}
                  >
                    {notification.course}
                  </Badge>
                </div>
                
                <div className="flex items-center text-xs text-slate-600 mb-1">
                  <MapPin className="w-3 h-3 mr-1" />
                  <span className="truncate">{notification.location}</span>
                </div>
                
                <p className="text-xs text-slate-500">{notification.time}</p>
              </div>
            </div>
            
            <button
              onClick={() => setNotifications(prev => 
                prev.filter((_, i) => i !== index)
              )}
              className="text-slate-400 hover:text-slate-600 transition-colors ml-2"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </Card>
      ))}
      
      {/* Toggle visibility button */}
      <div className="text-center">
        <button
          onClick={() => setIsVisible(false)}
          className="text-xs text-slate-500 hover:text-slate-700 transition-colors bg-white/80 px-3 py-1 rounded-full shadow-sm"
        >
          Hide notifications
        </button>
      </div>
      
      <style jsx>{`
        @keyframes slideInLeft {
          from {
            transform: translateX(-100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        .animate-slide-in-left {
          animation: slideInLeft 0.5s ease-out;
        }
      `}</style>
    </div>
  );
};

export default LiveNotifications;